import { useEffect, useRef } from 'react';

// Lightweight real-time refresh. Calls `callback` on an interval, and also
// immediately when the tab regains visibility/focus. Only fires while the tab
// is visible to save resources. `callback` MUST be safe to call repeatedly
// (idempotent, silent on transient errors) so it never disrupts the UI.
export default function usePolling(callback, { intervalMs = 15000, enabled = true } = {}) {
  const savedCb = useRef(callback);
  useEffect(() => { savedCb.current = callback; }, [callback]);

  useEffect(() => {
    if (!enabled) return undefined;
    const run = () => {
      if (document.visibilityState === 'visible') savedCb.current();
    };
    // intervalMs = 0 (or falsy) => no continuous polling; only refresh when the
    // tab regains focus/visibility. Used to eliminate background data usage
    // while still keeping data fresh when the user comes back to the screen.
    const id = intervalMs ? setInterval(run, intervalMs) : null;
    const onVisibility = () => { if (document.visibilityState === 'visible') savedCb.current(); };
    const onFocus = () => savedCb.current();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      if (id) clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [intervalMs, enabled]);
}
